import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { fmtMoney0, getDisplayCurrency } from '../../utils/currency';

const fmt = fmtMoney0;

/** Format a value in an explicit currency code (falls back to a plain label). */
function fmtAmt(value, code) {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code,
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(n);
  } catch (e) {
    return `${code} ${Math.round(n).toLocaleString('en-US')}`;
  }
}

/**
 * ACCOUNT BALANCES — collapsible status panel showing Cash, Bank
 * Accounts, Accounts Receivable, Accounts Payable and current balance.
 * Bank balances are split by currency (e.g. USD + AFN) and summed into the
 * company's base currency (Afghani) using the saved exchange rates.
 */
export default function AccountBalancePanel({ kpi = {}, balances = {} }) {
  const [open, setOpen] = useState(true);

  const cash = balances.cash || 0;
  const base = balances.baseCurrency || getDisplayCurrency() || 'USD';
  const rates = balances.rates || {};
  const bankByCurrency = balances.bankByCurrency || {};
  const curList = Object.keys(bankByCurrency);
  const bankTotal = curList.reduce(
    (s, c) => s + (bankByCurrency[c] || 0) * (c === base ? 1 : (rates[c] || 0)),
    0
  );
  const ar = kpi.accounts_receivable || 0;
  const ap = kpi.accounts_payable || 0;
  const total = cash + bankTotal + ar - ap;
  const docShort = (byCur) => (byCur || []).filter(c => parseFloat(c.amount) > 0)
    .map(c => `${c.currency} ${Math.round(parseFloat(c.amount)).toLocaleString('en-US')}`).join(' + ');
  const arDoc = docShort(kpi.accounts_receivable_by_currency);
  const apDoc = docShort(kpi.accounts_payable_by_currency);

  return (
    <section className="desk-status-section">
      <button type="button" className={`desk-status-toggle ${open ? '' : 'collapsed'}`} onClick={() => setOpen(o => !o)}>
        <ChevronDown /> Account Balances
      </button>
      {open && (
        <div className="desk-status-body">
          <div className="desk-balance-row"><span className="desk-balance-label">Cash ({base})</span><span className="desk-balance-value">{fmt(cash)}</span></div>
          <div className="desk-balance-row"><span className="desk-balance-label">Bank Accounts ({base})</span><span className="desk-balance-value">{fmt(bankTotal)}</span></div>
          {curList.map(c => (
            <div key={c} className="desk-balance-row desk-balance-sub">
              <span className="desk-balance-label">— {c} Balance</span>
              <span className="desk-balance-value">{fmtAmt(bankByCurrency[c], c)}</span>
            </div>
          ))}
          <div className="desk-balance-row"><span className="desk-balance-label">Accounts Receivable</span><span className="desk-balance-value">{fmt(ar)}{arDoc ? ` · ${arDoc}` : ''}</span></div>
          <div className="desk-balance-row"><span className="desk-balance-label">Accounts Payable</span><span className="desk-balance-value">{fmt(ap)}{apDoc ? ` · ${apDoc}` : ''}</span></div>
          <div className="desk-balance-row desk-balance-total"><span className="desk-balance-label">Current Balance</span><span className="desk-balance-value">{fmt(total)}</span></div>
        </div>
      )}
    </section>
  );
}